import { z } from 'zod';

export const stockTickerSchema = z.object({
  ticker: z.string(),
  name: z.string(),
});
export type StockTicker = z.infer<typeof stockTickerSchema>;

export const employeeStockPlanSchema = z.enum(['Exercised', 'ESPP', 'Restricted']);
export type EmployeeStockPlan = z.infer<typeof employeeStockPlanSchema>;

export const stockLotSchema = z.object({
  numberOfShares: z
    .number({ coerce: true, message: 'Shares must be greater than 0' })
    .int({ message: 'Shares must be a whole number' })
    .gt(0, { message: 'Shares must be greater than 0' }),
  purchasePrice: z
    .number({ coerce: true, message: 'Price must be greater than 0' })
    .gt(0, { message: 'Price must be greater than 0' }),
  purchaseDate: z
    .string()
    .date()
    .refine(
      v => {
        const today = new Date();
        const purchaseDate = new Date(v);
        return purchaseDate < today && purchaseDate >= new Date('1900-01-01');
      },
      { message: 'Inform a valid past date' },
    ),
  lotId: z
    .string()
    .optional()
    .transform(v => (v === '' ? undefined : v)),
  employeeStockPlan: employeeStockPlanSchema
    .or(z.literal('None'))
    .optional()
    .transform(v => (v === 'None' ? undefined : v)),
});

export type StockLot = z.infer<typeof stockLotSchema>;

export const stockBrokerageSchema = z.object({
  name: z.string().min(1, { message: 'Required' }),
  label: z.string().min(1, { message: 'Required' }),
  icon: z.string().optional(),
});
export type StockBrokerage = z.infer<typeof stockBrokerageSchema>;

export const stockBrokerageInfoSchema = z.object({
  identifier: z.string(),
  accountNumber: z.string().min(1, { message: 'Required' }),
  contactName: z.string().min(1, { message: 'Required' }),
  email: z.string().email(),
  phone: z
    .string()
    .refine(v => /^\s*(?:\+?(\d{1,3}))?[-. (]*(\d{3})[-. )]*(\d{3})[-. ]*(\d{4})(?: *x(\d+))?\s*$/im.test(v), {
      message: `Invalid phone number, please enter a number in the format '+X XXX-XXX-XXXX'`,
    }),
});
export type StockBrokerageInfo = z.infer<typeof stockBrokerageInfoSchema>;

// Static list of supported brokers with their icon paths
export const SUPPORTED_BROKERARGES: Array<StockBrokerage> = [
  {
    name: 'ameriprise',
    label: 'Ameriprise',
    icon: '/images/brokerages/ameriprise.png',
  },
  {
    name: 'schwab',
    label: 'Charles Schwab',
    icon: '/images/brokerages/schwab.png',
  },
  {
    name: 'etrade',
    label: 'E-Trade',
    icon: '/images/brokerages/etrade.png',
  },
  {
    name: 'fidelity',
    label: 'Fidelity Investments',
    icon: '/images/brokerages/fidelity.png',
  },
  {
    name: 'jpmorgan',
    label: 'J.P. Morgan',
    icon: '/images/brokerages/jpmorgan.png',
  },
  {
    name: 'lpl',
    label: 'LPL Financial',
    icon: '/images/brokerages/lpl.png',
  },
  {
    name: 'merrill',
    label: 'Merrill Lynch',
    icon: '/images/brokerages/merrill.png',
  },
  {
    name: 'morganstanley',
    label: 'Morgan Stanley',
    icon: '/images/brokerages/morganstanley.png',
  },
  {
    name: 'ameritrade',
    label: 'TD Ameritrade',
    icon: '/images/brokerages/ameritrade.png',
  },
  {
    name: 'tiaa',
    label: 'TIAA Brokerage',
    icon: '/images/brokerages/tiaa.png',
  },
  {
    name: 'trowe',
    label: 'T. Rowe Price',
    icon: '/images/brokerages/trowe.png',
  },
  {
    name: 'wellsfargo',
    label: 'Wells Fargo',
    icon: '/images/brokerages/wellsfargo.png',
  },
  {
    name: 'ubs',
    label: 'UBS',
  },
  {
    name: 'other',
    label: 'Other',
  },
];
